import moment from 'moment';

import { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import DataForm from 'components/ui/DataForm';
import AgendaForm from 'components/agenda/AgendaForm';
import DeviceForm from 'components/devices/DeviceForm';
import SubmitButton from 'components/ui/buttons/SubmitButton';
import withModalView from 'components/ui/withModalView';
import withContentLoader from 'components/ui/withContentLoader';
import withAuthorizer from 'components/ui/withAuthorizer';
import How2Install from 'components/explore/How2Install';

import { Resources } from 'helpers/ResourceHelper';

import JobApi from 'api/job';
import BotApi from 'api/bot';
import NotificationHelper from 'helpers/NotificationHelper';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AddJobPage = ({ data, backUrl }) => {
  const { t } = useTranslation();

  const { botId } = useParams();
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const query = useQuery();

  const deviceId = query.get('device');

  const bot = data || {};

  const config_data = bot.schemas.find(s => s.id === 'config') || { schema: { required: [] } };
  const settings_data = bot.settings.find(s => s.id === 'config') || { value: {} };
  const config_schema = config_data.schema || { required: [] };
  const config_schema_required = config_schema.required || [];
  const has_config = config_schema_required.length <= Object.keys(settings_data.value || []).length;

  const input_data = (bot.schemas || []).find(setting => setting.id === 'input_data') || {};
  const schema = input_data.schema || {};
  const uiSchema = input_data.ui_schema || {};

  schema.type = 'object';
  delete schema.title;

  const NoFormMessage = schema.properties ? null : <div>{t('bots.jobs.add.no_form_message')}</div>;

  const settings = {
    schema,
    uiSchema,
  };

  const renderConfigAlert = () => {
    if (has_config) {
      return null;
    }

    return (
      <div className="alert alert-warning">
        Esse robô ainda não foi configurado. Por favor, configure o robô <a href={`/bots/${botId}?tab=config`}>clicando aqui</a>.
      </div>
    );
  };

  const onSubmit = async e => {
    e.preventDefault();

    const form = document.querySelector('form#form-item');
    const elements = [...form.elements];

    for (const element of elements) {
      const prefix = 'root_';
      if (element.id.startsWith(prefix)) {
        element.setAttribute('name', element.id.replace(prefix, ''));
      }

      if (element.id.startsWith('rrule')) {
        element.removeAttribute('name');
      }
    }

    const hidden_input_device = document.querySelector('#hidden_input_device_id');

    if (hidden_input_device && !hidden_input_device.value) {
      alert('O dispositivo não foi informado.');
      return;
    }

    const formDataObj = new FormData(e.target);
    const formData = Object.fromEntries(formDataObj);

    setSaving(true);

    try {
      const bind = {
        bot_id: botId,
        start_at: `${formData.date} ${formData.time}:00${moment().format('Z')}`,
        title: formData.title || bot.name,
        rrule: formData.rrule,
        device_id: formData.device_id,
      };

      delete formData.date;
      delete formData.time;
      delete formData.title;
      delete formData.rrule;
      delete formData.device_id;

      bind.input_data = formData;

      await JobApi.create(bind);
      queryClient.invalidateQueries('agenda');
      history.push(backUrl);
    } catch (error) {
      NotificationHelper.snack(t('bots.jobs.add.error_add'));
    } finally {
      setSaving(false);
    }
  };

  const renderHow2Install = () => {
    if (bot.packageable) {
      return (
        <>
          <How2Install />
        </>
      );
    }

    return null;
  };

  return (
    <>
      <div className="container">
        <div className="mb-2">{t('bots.jobs.add.title')}</div>
        <h3>{bot.name}</h3>
      </div>

      <form className="container mt-4" id="form-item" onSubmit={onSubmit}>
        {renderConfigAlert()}
        {NoFormMessage}

        <DataForm disabled={!has_config} {...settings} tagName="div">
          <div></div>
        </DataForm>

        <DeviceForm disabled={!has_config} required={true} deviceId={deviceId} botId={botId} show={bot.packageable} />

        <hr />
        <p>
          <b>Agenda</b>
        </p>
        <AgendaForm disabled={!has_config} />
        <hr />

        <div className="text-right mb-5">
          <SubmitButton disabled={!has_config} className="btn btn-primary btn-lg" loading={saving} />
        </div>

        {renderHow2Install()}
      </form>
    </>
  );
};

const fetchData = async ({ routeMatch }) => {
  const { params } = routeMatch;
  return await BotApi.get(params.botId);
};

const page = withContentLoader(AddJobPage, { name: 'job-add', fetchData });
export default withModalView(withAuthorizer(page, [Resources.BOT_MANAGER]));
